import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  decideTextFieldSync,
  shouldShowLinkifiedDisplay,
  type WaveBlock,
  type WaveDocEditor,
  type WaveTextField,
} from "@vida/wave-core";
import { applyAssistantDirectiveOnBlur } from "../../services/waveAssistant";
import { renderLinkifiedText } from "../../services/waveLinkify";

export type DraftTextFieldEditHandlers = {
  onFocus?: () => void;
  onBlur?: () => void;
  onTypingActivity?: () => void;
};

export type DraftTextFieldProps = {
  waveId: string;
  block: WaveBlock;
  field: WaveTextField;
  editor?: WaveDocEditor | null;
  remoteContentRevision?: number;
  hasRemoteEditors?: boolean;
  readOnly?: boolean;
  multiline?: boolean;
  className: string;
  placeholder: string;
  onUpdate?: (block: WaveBlock) => void;
  onSelectionChange?: (offset: number, endOffset: number) => void;
  edit: DraftTextFieldEditHandlers;
};

function fitTextareaToContent(el: HTMLTextAreaElement) {
  el.style.height = "0";
  el.style.height = `${Math.max(el.scrollHeight, 44)}px`;
}

export function DraftTextField({
  waveId,
  block,
  field,
  editor,
  remoteContentRevision = 0,
  hasRemoteEditors = false,
  readOnly,
  multiline,
  className,
  placeholder,
  onUpdate,
  onSelectionChange,
  edit,
}: DraftTextFieldProps) {
  const external =
    field === "title"
      ? (block.payload.title ?? "")
      : (block.payload.text ?? "");
  const [draft, setDraft] = useState(external);
  const [displayEditing, setDisplayEditing] = useState(false);
  const editingRef = useRef(false);
  const lastRemoteRevisionRef = useRef(remoteContentRevision);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const showLinkifiedDisplay = shouldShowLinkifiedDisplay(displayEditing, draft);

  const displayClassName = multiline
    ? `${className} wave-text-display`
    : className.includes("wave-heading")
      ? `${className} wave-heading-display`
      : `${className} wave-text-display`;

  useEffect(() => {
    if (!displayEditing) return;
    const el = multiline ? textareaRef.current : inputRef.current;
    el?.focus();
  }, [displayEditing, multiline]);

  const syncTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (el) fitTextareaToContent(el);
  }, []);

  useLayoutEffect(() => {
    if (multiline && !showLinkifiedDisplay) syncTextareaHeight();
  }, [draft, multiline, showLinkifiedDisplay, syncTextareaHeight]);

  useEffect(() => {
    if (!multiline || showLinkifiedDisplay) return;
    const el = textareaRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => syncTextareaHeight());
    observer.observe(el);
    return () => observer.disconnect();
  }, [multiline, showLinkifiedDisplay, syncTextareaHeight]);

  useEffect(() => {
    const remoteRevisionBumped =
      remoteContentRevision !== lastRemoteRevisionRef.current;
    lastRemoteRevisionRef.current = remoteContentRevision;

    const { nextDraft, shouldSync } = decideTextFieldSync({
      external,
      draft,
      isEditing: editingRef.current,
      isFocused: displayEditing,
      allowRemoteOverwrite: hasRemoteEditors,
      remoteRevisionBumped,
    });

    if (shouldSync && nextDraft !== draft) {
      setDraft(nextDraft);
    }
  }, [block.id, external, draft, remoteContentRevision, hasRemoteEditors]);

  const commit = useCallback(
    (value: string) => {
      if (editor) {
        editor.setText(block.id, field, value);
        return;
      }
      onUpdate?.({
        ...block,
        payload: {
          ...block.payload,
          [field]: value,
        },
      });
    },
    [block, editor, field, onUpdate],
  );

  const handleBlur = useCallback(() => {
    setDisplayEditing(false);
    edit.onBlur?.();
    if (readOnly) return;
    void applyAssistantDirectiveOnBlur(waveId, draft, (integrated) => {
      setDraft(integrated);
      commit(integrated);
    });
  }, [waveId, draft, readOnly, edit, commit]);

  const enterDisplayEdit = useCallback(() => {
    if (readOnly) return;
    setDisplayEditing(true);
    edit.onFocus?.();
  }, [readOnly, edit]);

  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = e.currentTarget;
      onSelectionChange?.(target.selectionStart ?? 0, target.selectionEnd ?? 0);
    },
    [onSelectionChange],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      editingRef.current = true;
      edit.onTypingActivity?.();
      setDraft(e.target.value);
      commit(e.target.value);
      if (multiline && e.target instanceof HTMLTextAreaElement) {
        fitTextareaToContent(e.target);
      }
      window.setTimeout(() => {
        editingRef.current = false;
      }, 50);
    },
    [commit, edit, multiline],
  );

  const nativeFieldProps = {
    className,
    placeholder,
    readOnly,
    value: draft,
    onFocus: () => {
      setDisplayEditing(true);
      edit.onFocus?.();
    },
    onBlur: handleBlur,
    onSelect: handleSelect,
    onKeyUp: handleSelect,
    onMouseUp: handleSelect,
    onChange: handleChange,
    "aria-hidden": showLinkifiedDisplay ? true : undefined,
    tabIndex: showLinkifiedDisplay ? -1 : undefined,
    style: showLinkifiedDisplay
      ? ({ position: "absolute" as const, opacity: 0, pointerEvents: "none" as const, width: "100%" })
      : undefined,
  };

  return (
    <div className="wave-draft-field" style={{ position: "relative", width: "100%" }}>
      {multiline ? (
        <textarea {...nativeFieldProps} ref={textareaRef} rows={1} />
      ) : (
        <input {...nativeFieldProps} ref={inputRef} />
      )}

      {showLinkifiedDisplay && (
        <div
          role={readOnly ? undefined : "button"}
          tabIndex={readOnly ? undefined : 0}
          className={displayClassName}
          onClick={readOnly ? undefined : enterDisplayEdit}
          onKeyDown={
            readOnly
              ? undefined
              : (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    enterDisplayEdit();
                  }
                }
          }
        >
          {renderLinkifiedText(draft)}
        </div>
      )}
    </div>
  );
}
